/**
 * VCardPage.jsx — Carte de visite digitale INTERACTIVE (version WOW)
 *
 * Routes :
 *   /vcard, /b                            → version générique (sans tracking)
 *   /vcard/:slug, /b/:slug                 → version personnalisée pour un prospect
 *                                             (visites + scans tracés)
 *
 * NOUVEAU :
 *   - Bouton géant "➕ AJOUTER À MES CONTACTS" → télécharge .vcf natif (iPhone/Android)
 *   - Carte flip 3D (recto QR / verso détails) au tap
 *   - Partage natif Web Share API + fallback copie lien
 *   - Boutons d'action animés : Appeler / SMS / Email / Site
 *   - Section Prestations dépliable
 *   - Effet shine passant + QR qui pulse
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  Phone, Mail, Download, FileText, MapPin, Share2, Globe,
  Clock, CheckCircle2, UserPlus, MessageSquare, Sparkles,
  ChevronDown, Copy, Check, QrCode, Award, Send, X,
} from 'lucide-react';
import api, { API_URL } from '../api';
import { toast } from 'sonner';

const CARD = {
  name: 'Bruno Llorens',
  nameSort: 'Llorens Bruno',
  role: 'Enseignant de la conduite — Moto & Auto',
  cert: 'ECSR + CCS Deux Roues · Aut. A 26 038 0007 0 Isère',
  phone: '+33 7 82 26 82 00',
  phoneHref: 'tel:+33782268200',
  smsHref: 'sms:+33782268200',
  email: 'contact@code2roues.fr',
  emailHref: 'mailto:contact@code2roues.fr',
  site: 'code2roues.fr',
  siteHref: 'https://code2roues.fr',
  area: 'Saint-Savin (38300) · Nord Isère · Est Lyonnais · Drôme',
  hours: 'Disponible 7j/7 · 08h00-20h00',
  vcf: '/bruno-llorens.vcf',
  avatar: '/bruno-avatar.jpg',
};

const PRESTATIONS = [
  { icon: '🏍️', label: 'Permis A1 / A2', sub: '14 ans / 18 ans' },
  { icon: '📜', label: 'Formation 125 (7h)', sub: 'Pour permis voiture' },
  { icon: '🔥', label: 'Passerelle A', sub: '2 ans de permis A2' },
  { icon: '🎯', label: 'Perfectionnement', sub: 'Trajectoires, freinage' },
  { icon: '🚗', label: 'Préparation Permis B', sub: 'Technique + examen' },
  { icon: '⚡', label: 'Examen blanc', sub: 'Mise en situation IPCSR' },
];

export default function VCardPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  // 🔐 Mode propriétaire activé via ?pro=1 (ou ?me=1) — URL privée que vous seul utilisez
  // En mode visiteur (par défaut) → messages de recommandation neutres
  // En mode pro → messages de candidature spontanée pour dirigeants d'auto-école
  const isOwnerMode = searchParams.get('pro') === '1' || searchParams.get('me') === '1';
  const [prospect, setProspect] = useState(null);
  const [loading, setLoading] = useState(!!slug);
  const [flipped, setFlipped] = useState(false);
  const [showPrestations, setShowPrestations] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  // 🔑 URL de production (fixe) pour le partage externe —
  // garantit que WhatsApp/SMS/Mail reçoivent toujours le lien PROD avec les bons OG,
  // même si vous testez depuis une preview emergentagent.
  // ⚠️ TOUJOURS vers /carte (page statique avec OG propre). Jamais vers /vcard (SPA, OG = site racine).
  const PROD_URL = 'https://code2roues.fr';
  const shareUrl = useMemo(
    () => (slug ? `${PROD_URL}/carte?s=${slug}` : `${PROD_URL}/carte`),
    [slug]
  );

  // URL de tracking (passe par backend qui log et redirige)
  const linkFor = useMemo(() => (action) => {
    if (slug) return `${API_URL}/api/vcard/track/${slug}/${action}`;
    switch (action) {
      case 'cv':      return `${origin}/assets/bruno-llorens-cv-2027.pdf`;
      case 'vcf':     return `${origin}${CARD.vcf}`;
      case 'call':    return CARD.phoneHref;
      case 'sms':     return CARD.smsHref;
      case 'email':   return CARD.emailHref;
      case 'website': return CARD.siteHref;
      default: return '#';
    }
  }, [slug, origin]);

  const qrUrl = useMemo(() => shareUrl, [shareUrl]);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!slug) { setLoading(false); return; }
      try {
        const p = await api.vcardGetProspectPublic(slug);
        if (active && p?.found) setProspect(p);
        api.vcardRegisterVisit(slug);
      } catch { /* soft fail */ }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [slug]);

  const prospectGreeting = prospect?.prospect_name
    ? `Bonjour ${prospect.prospect_name.split(' ')[0]}`
    : null;

  // ───── TEMPLATES de partage — adaptés selon le mode ─────
  // 🔐 OWNER (vous, candidature spontanée) : URL /vcard?pro=1
  // 👥 VISITOR (vos prospects, recommandation) : URL /vcard (par défaut)
  const OWNER_TEMPLATES = {
    sms: `Bonjour,
Je me permets de vous laisser mes coordonnées professionnelles pour un éventuel renfort ou remplacement ponctuel sur vos formations.

Bruno Llorens — Enseignant de la conduite · Moto & Auto
ECSR + CCS 2R · Nord Isère / Est Lyonnais / Drôme
Disponible 7j/7 · 08h-20h

Ma carte complète (ajout aux contacts en 1 tap) :
${shareUrl}

Cordialement,
Bruno`,
    email: {
      subject: 'Bruno Llorens — Enseignant de la conduite (Moto & Auto)',
      body: `Bonjour,

Je me permets de me présenter : Bruno Llorens, enseignant de la conduite Moto & Auto, titulaire du TP ECSR et du CCS 2 Roues (agrément préfectoral valide jusqu'au 08/07/2032).

Basé à Saint-Savin (38300), j'interviens sur le Nord Isère, l'Est Lyonnais et la Drôme pour :
• Formation 125 (7h) pour titulaires du permis voiture
• Passerelle A2 → A (7h)
• Préparation examens plateau & circulation (A1 / A2)
• Perfectionnement, trajectoires, freinage d'urgence
• Préparation Permis B (circulation, examen blanc)
• Renfort ponctuel ou remplacement moniteur

Je reste disponible 7j/7 de 08h à 20h au 07 82 26 82 00 pour échanger sur vos besoins.

Ma carte de visite digitale (avec CV, QR code et ajout direct aux contacts) :
${shareUrl}

Cordialement,
Bruno Llorens
📞 07 82 26 82 00
✉ contact@code2roues.fr
🌐 code2roues.fr`,
    },
    simple: `Bruno Llorens — Enseignant de la conduite · Moto & Auto
07 82 26 82 00 · Disponible 7j/7
${shareUrl}`,
  };

  const VISITOR_TEMPLATES = {
    sms: `Je te partage la carte de Bruno Llorens, enseignant de la conduite (Moto & Auto).

Basé en Nord Isère / Est Lyonnais / Drôme — dispo 7j/7 au 07 82 26 82 00.

Sa carte complète (ajout aux contacts en 1 tap) :
${shareUrl}`,
    email: {
      subject: 'Carte de visite — Bruno Llorens (Enseignant de la conduite Moto & Auto)',
      body: `Bonjour,

Je te partage les coordonnées de Bruno Llorens, enseignant de la conduite (titulaire du TP ECSR et du CCS 2 Roues).

Il intervient sur le Nord Isère, l'Est Lyonnais et la Drôme pour :
• Permis A1 / A2 (14 ans / 18 ans)
• Formation 125 (7h) pour permis voiture
• Passerelle A2 → A
• Permis B et perfectionnement moto

Il est disponible 7j/7 de 08h à 20h au 07 82 26 82 00.

Sa carte de visite digitale (avec QR code et ajout direct aux contacts) :
${shareUrl}

Bonne journée.`,
    },
    simple: `Bruno Llorens — Enseignant de la conduite · Moto & Auto
07 82 26 82 00 · Dispo 7j/7
${shareUrl}`,
  };

  const SHARE_TEMPLATES = isOwnerMode ? OWNER_TEMPLATES : VISITOR_TEMPLATES;

  const shareViaSMS = () => {
    const body = encodeURIComponent(SHARE_TEMPLATES.sms);
    // Format compatible iOS + Android récents
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const href = isIOS ? `sms:&body=${body}` : `sms:?body=${body}`;
    window.location.href = href;
    setShowShareMenu(false);
  };

  const shareViaEmail = () => {
    const { subject, body } = SHARE_TEMPLATES.email;
    const href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
    setShowShareMenu(false);
  };

  const shareViaNative = async () => {
    const data = {
      title: `${CARD.name} — ${CARD.role}`,
      text: SHARE_TEMPLATES.simple,
      url: shareUrl,
    };
    if (navigator.share) {
      try { await navigator.share(data); setShowShareMenu(false); }
      catch { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Lien copié', { description: 'Collez-le où vous voulez.' });
      setTimeout(() => setCopied(false), 2000);
      setShowShareMenu(false);
    } catch {
      toast.error('Impossible de copier');
    }
  };

  const copyProMessage = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_TEMPLATES.sms);
      toast.success('Message pro copié', { description: 'Collez-le dans WhatsApp, LinkedIn, Signal…' });
      setShowShareMenu(false);
    } catch {
      toast.error('Impossible de copier');
    }
  };

  const handleShare = () => setShowShareMenu(true);

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white relative overflow-hidden"
      data-testid="vcard-page"
    >
      {/* Décor global : halos lumineux */}
      <div aria-hidden="true" className="absolute -top-32 -left-20 w-96 h-96 bg-orange-600/20 rounded-full blur-3xl pointer-events-none" />
      <div aria-hidden="true" className="absolute top-1/3 -right-24 w-80 h-80 bg-amber-500/15 rounded-full blur-3xl pointer-events-none" />
      <div aria-hidden="true" className="absolute bottom-0 left-1/4 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-md mx-auto px-4 py-6 md:py-10">

        {/* Greeting personnalisé */}
        {prospectGreeting && (
          <div
            className="flex items-center justify-center gap-1.5 text-xs tracking-widest uppercase text-orange-400 mb-4 animate-fade-in"
            data-testid="vcard-greeting"
          >
            <Sparkles className="w-3.5 h-3.5" />
            {prospectGreeting}, voici ma carte
          </div>
        )}

        {/* ═══════════ CARTE PRINCIPALE (3D flip au tap) ═══════════ */}
        <div
          className="relative mb-5"
          style={{ perspective: '2000px' }}
        >
          <div
            className={`relative transition-transform duration-700 ease-out`}
            style={{
              transformStyle: 'preserve-3d',
              transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
            }}
          >
            {/* ───────── RECTO ───────── */}
            <div
              className="relative bg-gradient-to-br from-slate-800/95 via-slate-900/95 to-slate-800/95 backdrop-blur-xl border border-orange-400/30 rounded-3xl p-6 md:p-7 shadow-[0_20px_60px_-15px_rgba(249,115,22,0.4)] overflow-hidden"
              style={{ backfaceVisibility: 'hidden' }}
            >
              {/* Shine passant */}
              <div
                aria-hidden="true"
                className="absolute inset-0 overflow-hidden rounded-3xl pointer-events-none"
              >
                <div className="absolute -inset-x-20 -inset-y-20 bg-gradient-to-br from-transparent via-white/[0.06] to-transparent rotate-12 animate-vcard-shine" />
              </div>

              {/* Halos décoratifs */}
              <div aria-hidden="true" className="absolute -top-24 -right-24 w-56 h-56 bg-orange-500/25 rounded-full blur-3xl pointer-events-none" />
              <div aria-hidden="true" className="absolute -bottom-24 -left-16 w-48 h-48 bg-amber-500/15 rounded-full blur-3xl pointer-events-none" />

              {/* Badge Disponibilité */}
              <div className="relative flex justify-center mb-5">
                <div
                  className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-400/40 backdrop-blur-sm"
                  data-testid="vcard-badge-dispo"
                >
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                  </span>
                  <span className="text-[11px] font-black uppercase tracking-[0.14em] text-emerald-200">
                    Disponible 7j/7 · 08h-20h
                  </span>
                </div>
              </div>

              {/* Avatar photo (fichier généré .jpg) */}
              <div className="relative flex justify-center mb-3">
                <div className="relative">
                  <div aria-hidden="true" className="absolute inset-0 -m-2 rounded-full bg-gradient-to-br from-orange-400 via-amber-500 to-orange-600 blur-lg opacity-80 animate-pulse-slow" />
                  <img
                    src={CARD.avatar}
                    alt={CARD.name}
                    className="relative w-28 h-28 rounded-full object-cover shadow-2xl ring-4 ring-slate-900/80"
                    loading="eager"
                  />
                  {/* Indicateur en ligne */}
                  <div className="absolute bottom-1 right-1 w-5 h-5 rounded-full bg-emerald-500 border-[3px] border-slate-900 shadow-lg" />
                </div>
              </div>

              <div className="text-center">
                <h1
                  className="text-3xl md:text-[32px] font-bold text-white leading-tight"
                  style={{ fontFamily: 'Fraunces, serif' }}
                  data-testid="vcard-name"
                >
                  {CARD.name}
                </h1>
                <p className="text-sm text-orange-300 mt-1.5 font-bold">
                  {CARD.role}
                </p>
                <p className="text-[11px] text-slate-400 mt-1">
                  {CARD.cert}
                </p>
              </div>

              {/* ★★★ BOUTON HERO : AJOUTER AUX CONTACTS ★★★ */}
              <a
                href={linkFor('vcf')}
                download="Bruno-Llorens.vcf"
                className="relative mt-5 flex items-center justify-center gap-2.5 bg-gradient-to-r from-orange-500 via-orange-500 to-amber-500 hover:from-orange-600 hover:via-orange-600 hover:to-amber-600 text-slate-950 font-black text-base py-4 px-5 rounded-2xl shadow-[0_10px_30px_-5px_rgba(249,115,22,0.6)] transition-all hover:scale-[1.02] active:scale-[0.98] group overflow-hidden"
                data-testid="vcard-cta-add-contact"
              >
                {/* Shine effect sur hover */}
                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
                <UserPlus className="w-5 h-5 relative z-10" strokeWidth={2.5} />
                <span className="relative z-10 tracking-tight">Ajouter à mes contacts</span>
                <Sparkles className="w-4 h-4 relative z-10 opacity-80" />
              </a>

              {/* Téléphone GÉANT + CTA */}
              <div
                className="relative mt-4 bg-gradient-to-br from-orange-500/[0.12] via-amber-500/[0.06] to-transparent border border-orange-400/30 rounded-2xl p-4"
                data-testid="vcard-phone-hero"
              >
                <div className="text-[9.5px] uppercase tracking-[0.22em] text-orange-300 font-black mb-1 text-center">
                  Un seul numéro · 7j/7
                </div>
                <a
                  href={linkFor('call')}
                  className="block text-[28px] md:text-[30px] font-black text-white tracking-tight tabular-nums text-center hover:text-orange-200 transition"
                  style={{ fontFamily: 'Fraunces, serif' }}
                  data-testid="vcard-phone-big"
                >
                  {CARD.phone}
                </a>
                <div className="grid grid-cols-3 gap-2 mt-3">
                  <a
                    href={linkFor('call')}
                    className="group inline-flex flex-col items-center justify-center gap-1 h-14 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-slate-950 font-black transition-all hover:scale-[1.03] active:scale-[0.97] shadow-lg"
                    data-testid="vcard-cta-call"
                  >
                    <Phone className="w-4 h-4 group-hover:scale-110 transition-transform" strokeWidth={2.5} />
                    <span className="text-[10px] tracking-wider">APPEL</span>
                  </a>
                  <a
                    href={linkFor('sms')}
                    className="group inline-flex flex-col items-center justify-center gap-1 h-14 rounded-xl border border-white/15 bg-white/[0.06] hover:bg-white/[0.12] text-white font-bold transition-all hover:scale-[1.03] active:scale-[0.97]"
                    data-testid="vcard-cta-sms"
                  >
                    <MessageSquare className="w-4 h-4 group-hover:scale-110 transition-transform text-orange-300" strokeWidth={2.5} />
                    <span className="text-[10px] tracking-wider">SMS</span>
                  </a>
                  <a
                    href={linkFor('email')}
                    className="group inline-flex flex-col items-center justify-center gap-1 h-14 rounded-xl border border-white/15 bg-white/[0.06] hover:bg-white/[0.12] text-white font-bold transition-all hover:scale-[1.03] active:scale-[0.97]"
                    data-testid="vcard-cta-email"
                  >
                    <Mail className="w-4 h-4 group-hover:scale-110 transition-transform text-amber-300" strokeWidth={2.5} />
                    <span className="text-[10px] tracking-wider">MAIL</span>
                  </a>
                </div>
              </div>

              {/* Localisation + Site */}
              <div className="mt-4 pt-4 border-t border-white/10 space-y-2.5">
                <div className="flex items-start gap-2.5 text-[13px] text-slate-200">
                  <MapPin className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />
                  <span>{CARD.area}</span>
                </div>
                <a
                  href={linkFor('website')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 text-[13px] text-slate-200 hover:text-orange-300 transition"
                  data-testid="vcard-link-site"
                >
                  <Globe className="w-4 h-4 text-orange-400 shrink-0" />
                  <span>{CARD.site}</span>
                </a>
              </div>

              {/* Flip hint + QR button */}
              <button
                type="button"
                onClick={() => setFlipped(true)}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-xs font-bold tracking-wider text-slate-300 hover:text-white transition-all"
                data-testid="vcard-btn-show-qr"
              >
                <QrCode className="w-4 h-4" />
                Afficher mon QR code
                <ChevronDown className="w-3.5 h-3.5 -rotate-90" />
              </button>
            </div>

            {/* ───────── VERSO (QR code géant) ───────── */}
            <div
              className="absolute inset-0 bg-gradient-to-br from-slate-800/95 via-slate-900/95 to-slate-800/95 backdrop-blur-xl border border-orange-400/30 rounded-3xl p-6 md:p-7 shadow-[0_20px_60px_-15px_rgba(249,115,22,0.4)] overflow-hidden flex flex-col"
              style={{
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)',
              }}
            >
              <div aria-hidden="true" className="absolute -top-20 -left-20 w-56 h-56 bg-orange-500/20 rounded-full blur-3xl pointer-events-none" />
              <div aria-hidden="true" className="absolute -bottom-24 -right-16 w-48 h-48 bg-amber-500/15 rounded-full blur-3xl pointer-events-none" />

              <div className="relative text-center">
                <div className="inline-flex items-center gap-1.5 text-[10px] tracking-widest uppercase font-black text-orange-300 mb-2">
                  <QrCode className="w-3.5 h-3.5" /> Scannez pour ma carte
                </div>
                <h2
                  className="text-xl font-bold text-white"
                  style={{ fontFamily: 'Fraunces, serif' }}
                >
                  {CARD.name}
                </h2>
                <p className="text-[11px] text-orange-300 mt-0.5 font-bold">
                  {CARD.role}
                </p>
              </div>

              {/* QR géant avec halo pulse */}
              <div className="relative flex-1 flex items-center justify-center my-4">
                <div aria-hidden="true" className="absolute inset-0 flex items-center justify-center">
                  <div className="w-64 h-64 bg-orange-500/20 rounded-3xl blur-2xl animate-pulse-slow" />
                </div>
                <div className="relative bg-white p-4 rounded-2xl shadow-2xl ring-2 ring-orange-400/50">
                  <QRCodeSVG value={qrUrl} size={220} level="M" includeMargin={false} />
                </div>
              </div>

              <div className="relative text-center">
                <div className="text-[11px] text-slate-300 font-semibold">{CARD.phone}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{shareUrl.replace(/^https?:\/\//, '')}</div>
              </div>

              <button
                type="button"
                onClick={() => setFlipped(false)}
                className="relative mt-4 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl bg-orange-500/15 border border-orange-400/40 hover:bg-orange-500/25 text-xs font-bold tracking-wider text-orange-200 transition-all"
                data-testid="vcard-btn-back"
              >
                <ChevronDown className="w-3.5 h-3.5 rotate-90" />
                Retour à la carte
              </button>
            </div>
          </div>
        </div>

        {/* ═══════════ ACTIONS RAPIDES ═══════════ */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <button
            type="button"
            onClick={handleShare}
            className="group flex items-center justify-center gap-2 h-14 rounded-2xl bg-white/[0.06] border border-white/15 hover:bg-white/[0.12] hover:border-white/25 text-white font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
            data-testid="vcard-btn-share"
          >
            {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Share2 className="w-4 h-4 group-hover:scale-110 transition-transform" />}
            <span className="text-xs tracking-wider">{copied ? 'LIEN COPIÉ' : 'PARTAGER'}</span>
          </button>
          <a
            href="/cv"
            className="group flex items-center justify-center gap-2 h-14 rounded-2xl bg-gradient-to-br from-blue-500/25 to-indigo-500/15 border border-blue-400/40 hover:bg-blue-500/35 hover:border-blue-400/60 text-white font-bold transition-all hover:scale-[1.02] active:scale-[0.98] relative overflow-hidden"
            data-testid="vcard-btn-cv-interactive"
          >
            {/* Shine passant */}
            <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
            <Sparkles className="w-4 h-4 text-blue-200 group-hover:scale-110 transition-transform relative z-10" />
            <span className="text-xs tracking-wider relative z-10">CV INTERACTIF</span>
          </a>
        </div>

        {/* Lien PDF discret en option */}
        <a
          href={linkFor('cv')}
          download="bruno-llorens-cv-2027.pdf"
          className="group flex items-center justify-center gap-1.5 py-2 rounded-xl text-[11px] font-bold tracking-wider text-slate-400 hover:text-orange-300 transition mb-4"
          data-testid="vcard-btn-cv-pdf"
        >
          <Download className="w-3.5 h-3.5" />
          ou télécharger mon CV au format PDF
        </a>

        {/* ═══════════ SECTION PRESTATIONS (dépliable) ═══════════ */}
        <div className="rounded-2xl bg-white/[0.04] border border-white/10 overflow-hidden mb-4">
          <button
            type="button"
            onClick={() => setShowPrestations((v) => !v)}
            className="w-full flex items-center justify-between gap-3 px-4 py-3.5 hover:bg-white/[0.03] transition"
            data-testid="vcard-btn-prestations"
          >
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-orange-500/25 to-amber-500/15 border border-orange-400/30 flex items-center justify-center">
                <Award className="w-4 h-4 text-orange-300" />
              </div>
              <div className="text-left">
                <div className="text-sm font-bold text-white">Mes prestations</div>
                <div className="text-[10px] text-slate-400 uppercase tracking-wider">Formations & accompagnement</div>
              </div>
            </div>
            <ChevronDown
              className={`w-4 h-4 text-slate-400 transition-transform ${showPrestations ? 'rotate-180' : ''}`}
            />
          </button>
          <div
            className={`grid transition-all duration-500 ease-out ${
              showPrestations ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
            }`}
          >
            <div className="overflow-hidden">
              <div className="grid grid-cols-2 gap-2 px-3 pb-3">
                {PRESTATIONS.map((p, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2 bg-white/[0.04] border border-white/10 rounded-xl p-2.5 hover:bg-white/[0.08] transition"
                    data-testid={`vcard-prestation-${i}`}
                  >
                    <span className="text-xl shrink-0">{p.icon}</span>
                    <div className="min-w-0">
                      <div className="text-[11px] font-bold text-white leading-tight">{p.label}</div>
                      <div className="text-[9.5px] text-slate-400 leading-tight mt-0.5">{p.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ═══════════ GARANTIES / SIGNATURE PRO ═══════════ */}
        <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-4 space-y-2.5">
          <KeyPoint icon={CheckCircle2}>ECSR + CCS Deux Roues · Agrément valide jusqu'au 08/07/2032</KeyPoint>
          <KeyPoint icon={Clock}>Disponible 7j/7 · 08h00-20h00</KeyPoint>
          <KeyPoint icon={MapPin}>Nord Isère · Est Lyonnais · Drôme</KeyPoint>
        </div>

        <p className="text-center text-[10px] text-slate-500 mt-6">
          {CARD.nameSort} · Saint-Savin 38300 · {CARD.site}
          {slug && <span className="block mt-1 text-slate-600">Réf : {slug}</span>}
        </p>
      </div>

      {/* ═══════════ MODAL : CHOIX DU CANAL DE PARTAGE ═══════════ */}
      {showShareMenu && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in"
          onClick={() => setShowShareMenu(false)}
          data-testid="vcard-share-menu"
        >
          <div
            className="relative w-full max-w-md bg-gradient-to-br from-slate-900 to-slate-950 border border-orange-400/30 rounded-3xl p-5 shadow-[0_20px_60px_-15px_rgba(249,115,22,0.5)]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowShareMenu(false)}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/5 hover:bg-white/15 flex items-center justify-center transition"
              aria-label="Fermer"
              data-testid="vcard-share-close"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>

            <div className="text-center mb-5">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-orange-500/15 border border-orange-400/40 mb-3">
                <Send className="w-5 h-5 text-orange-300" />
              </div>
              <h3
                className="text-lg font-bold text-white"
                style={{ fontFamily: 'Fraunces, serif' }}
              >
                {isOwnerMode ? 'Partager ma carte' : 'Recommander Bruno'}
              </h3>
              <p className="text-[11px] text-slate-400 mt-1">
                {isOwnerMode
                  ? 'Messages de candidature spontanée'
                  : 'Chaque canal utilise un message adapté'}
              </p>
              {isOwnerMode && (
                <div className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full bg-orange-500/15 border border-orange-400/40">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" />
                  <span className="text-[9.5px] font-black uppercase tracking-widest text-orange-200">
                    Mode pro
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-2.5">
              {/* SMS */}
              <button
                type="button"
                onClick={shareViaSMS}
                className="w-full flex items-start gap-3 p-3.5 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-400/30 hover:bg-emerald-500/20 hover:border-emerald-400/50 transition text-left group"
                data-testid="vcard-share-sms"
              >
                <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                  <MessageSquare className="w-5 h-5 text-emerald-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-white text-sm">
                    {isOwnerMode ? 'SMS — Candidature pro' : 'SMS — Recommandation'}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5 leading-snug">
                    {isOwnerMode
                      ? 'Candidature spontanée courte avec vos coordonnées complètes et lien carte'
                      : 'Message court pour partager la carte à un ami ou collègue'}
                  </div>
                </div>
              </button>

              {/* Email */}
              <button
                type="button"
                onClick={shareViaEmail}
                className="w-full flex items-start gap-3 p-3.5 rounded-2xl bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-400/30 hover:bg-blue-500/20 hover:border-blue-400/50 transition text-left group"
                data-testid="vcard-share-email"
              >
                <div className="w-10 h-10 rounded-xl bg-blue-500/20 border border-blue-400/40 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                  <Mail className="w-5 h-5 text-blue-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-white text-sm">
                    {isOwnerMode ? 'Email — Présentation complète' : 'Email — Recommandation'}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5 leading-snug">
                    {isOwnerMode
                      ? 'Email structuré avec toutes vos prestations détaillées et agrément'
                      : 'Email neutre pour transmettre la carte à un contact'}
                  </div>
                </div>
              </button>

              {/* Copier le message */}
              <button
                type="button"
                onClick={copyProMessage}
                className="w-full flex items-start gap-3 p-3.5 rounded-2xl bg-gradient-to-br from-orange-500/10 to-orange-600/5 border border-orange-400/30 hover:bg-orange-500/20 hover:border-orange-400/50 transition text-left group"
                data-testid="vcard-share-copy-pro"
              >
                <div className="w-10 h-10 rounded-xl bg-orange-500/20 border border-orange-400/40 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                  <Copy className="w-5 h-5 text-orange-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-white text-sm">
                    {isOwnerMode ? 'Copier le message pro' : 'Copier le message'}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5 leading-snug">
                    Pour WhatsApp, LinkedIn, Signal, Telegram… à coller où vous voulez
                  </div>
                </div>
              </button>

              {/* Partage natif / simple */}
              <button
                type="button"
                onClick={shareViaNative}
                className="w-full flex items-start gap-3 p-3.5 rounded-2xl bg-white/[0.04] border border-white/15 hover:bg-white/[0.08] hover:border-white/25 transition text-left group"
                data-testid="vcard-share-native"
              >
                <div className="w-10 h-10 rounded-xl bg-white/[0.08] border border-white/15 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                  <Share2 className="w-5 h-5 text-slate-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-white text-sm">Partage rapide</div>
                  <div className="text-[11px] text-slate-400 mt-0.5 leading-snug">
                    Message court — menu natif du téléphone
                  </div>
                </div>
              </button>

              {/* Lien seul */}
              <button
                type="button"
                onClick={copyLink}
                className="w-full flex items-start gap-3 p-3.5 rounded-2xl bg-white/[0.04] border border-white/15 hover:bg-white/[0.08] hover:border-white/25 transition text-left group"
                data-testid="vcard-share-link-only"
              >
                <div className="w-10 h-10 rounded-xl bg-white/[0.08] border border-white/15 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                  {copied ? <Check className="w-5 h-5 text-emerald-400" /> : <Globe className="w-5 h-5 text-slate-300" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-white text-sm">
                    {copied ? 'Lien copié ✓' : 'Copier le lien seul'}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5 leading-snug truncate">
                    {shareUrl.replace(/^https?:\/\//, '')}
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Animations keyframes */}
      <style>{`
        @keyframes vcard-shine {
          0% { transform: translateX(-100%) rotate(12deg); }
          100% { transform: translateX(100%) rotate(12deg); }
        }
        .animate-vcard-shine { animation: vcard-shine 8s ease-in-out infinite; }
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.05); }
        }
        .animate-pulse-slow { animation: pulse-slow 3s ease-in-out infinite; }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.4s ease-out; }
      `}</style>
    </div>
  );
}

// ---------- Sous-composants ----------
function KeyPoint({ icon: Icon, children }) {
  return (
    <div className="flex items-start gap-2.5 text-[12.5px] text-slate-300">
      <Icon className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />
      <span>{children}</span>
    </div>
  );
}
